import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, MeetingRoom } from '@mui/icons-material';

/** Ein Raum-Entwurf in der Standort-Bearbeitung. idRoom < 0 = noch nicht
 *  gespeichert (neu). Persistiert wird erst beim Speichern des Standorts. */
export interface IRoomDraftItem {
  idRoom: number;
  Name: string;
  Capacity: number | null;
}

interface IProps {
  rooms: IRoomDraftItem[];
  onChange: (rooms: IRoomDraftItem[]) => void;
}

/** Verwaltung der Raeume eines Standorts (Name + Kapazitaet/Personen) als
 *  reine Entwurfsliste. Aenderungen (Hinzufuegen/Bearbeiten/Loeschen) wirken
 *  lokal und werden erst beim Speichern des Standorts uebernommen. */
export const RoomManager: React.FC<IProps> = ({ rooms, onChange }) => {
  const [newName, setNewName] = useState('');
  const [newCapacity, setNewCapacity] = useState('');
  // Temporaere negative IDs fuer neue Raeume (eindeutig je Sitzung).
  const [tmpIdCounter, setTmpIdCounter] = useState(-1);

  const patchRoom = (idRoom: number, patch: Partial<IRoomDraftItem>) =>
    onChange(rooms.map(r => (r.idRoom === idRoom ? { ...r, ...patch } : r)));

  const removeRoom = (idRoom: number) =>
    onChange(rooms.filter(r => r.idRoom !== idRoom));

  const addRoom = () => {
    if (!newName.trim()) return;
    onChange([
      ...rooms,
      {
        idRoom: tmpIdCounter,
        Name: newName.trim(),
        Capacity: newCapacity === '' ? null : Number(newCapacity),
      },
    ]);
    setTmpIdCounter(c => c - 1);
    setNewName('');
    setNewCapacity('');
  };

  return (
    <Box>
      <Typography
        variant='subtitle1'
        sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
      >
        <MeetingRoom fontSize='small' />
        Räume ({rooms.length})
      </Typography>

      {rooms.map(room => (
        <Box
          key={room.idRoom}
          sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}
        >
          <TextField
            size='small'
            label='Raumname'
            value={room.Name}
            onChange={event =>
              patchRoom(room.idRoom, { Name: event.target.value })
            }
            sx={{ flex: 1 }}
          />
          <TextField
            size='small'
            type='number'
            label='Personen'
            value={room.Capacity === null ? '' : String(room.Capacity)}
            onChange={event =>
              patchRoom(room.idRoom, {
                Capacity:
                  event.target.value === '' ? null : Number(event.target.value),
              })
            }
            sx={{ width: 120 }}
          />
          <Tooltip title='Raum entfernen'>
            <IconButton
              size='small'
              color='error'
              onClick={() => removeRoom(room.idRoom)}
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      ))}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
        <TextField
          size='small'
          label='Neuer Raum'
          value={newName}
          onChange={event => setNewName(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter') addRoom();
          }}
          sx={{ flex: 1 }}
        />
        <TextField
          size='small'
          type='number'
          label='Personen'
          value={newCapacity}
          onChange={event => setNewCapacity(event.target.value)}
          sx={{ width: 120 }}
        />
        <Button
          size='small'
          variant='outlined'
          startIcon={<Add />}
          disabled={!newName.trim()}
          onClick={addRoom}
        >
          Hinzufügen
        </Button>
      </Box>
      <Typography variant='caption' color='text.secondary'>
        Änderungen an den Räumen werden erst beim Speichern des Standorts
        übernommen.
      </Typography>
    </Box>
  );
};
